import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './farmtotal.css';

const MonthlyQtyTotal = () => {
  const [farmers, setFarmers] = useState([]);
  const [records, setRecords] = useState([]);
  const [advances, setAdvances] = useState([]);

  const [selectedFarmid, setSelectedFarmid] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [totalQty, setTotalQty] = useState(0);
  const [price, setPrice] = useState('');
  const [calculatedAmount, setCalculatedAmount] = useState(null);

  const [advance, setAdvance] = useState(0);
  const [otherAdvance, setOtherAdvance] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [carryForwarded, setCarryForwarded] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchFarmers();
    fetchRecords();
    fetchAdvances();
  }, []);

  useEffect(() => {
    calculateTotalQty();
    calculateMonthlyAdvance();
    setCalculatedAmount(null);
    setCarryForwarded(false);
  }, [selectedFarmid, selectedMonth, selectedYear, records, advances]);

  const fetchFarmers = async () => {
    const res = await fetch('https://kmtea.onrender.com/farmers');
    const data = await res.json();
    setFarmers(data);
  };

  const fetchRecords = async () => {
    const res = await fetch('https://kmtea.onrender.com/collecttea');
    const data = await res.json();
    setRecords(data);
  };

  const fetchAdvances = async () => {
    const res = await fetch('https://kmtea.onrender.com/advances');
    const data = await res.json();
    setAdvances(data);
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const calculateTotalQty = () => {
    if (!selectedFarmid || !selectedMonth || !selectedYear) {
      setTotalQty(0);
      return;
    }

    const filtered = records.filter((r) => {
      const d = new Date(r.date);
      return (
        r.farmid === selectedFarmid &&
        d.getMonth() + 1 === parseInt(selectedMonth) &&
        d.getFullYear() === parseInt(selectedYear)
      );
    });

    const total = filtered.reduce((sum, r) => sum + Number(r.netweight || 0), 0);
    setTotalQty(total);
  };

  const calculateMonthlyAdvance = () => {
    if (!selectedFarmid || !selectedMonth || !selectedYear) return;

    const monthAdvances = advances.filter((a) => {
      const d = new Date(a.date);
      return (
        a.farmid === selectedFarmid &&
        d.getMonth() + 1 === parseInt(selectedMonth) &&
        d.getFullYear() === parseInt(selectedYear)
      );
    });

    const normal = monthAdvances
      .filter((a) => a.description?.toLowerCase() === 'advance' || a.description?.toLowerCase() === 'carry forward')
      .reduce((sum, a) => sum + Number(a.amount || 0), 0);

    const others = monthAdvances
      .filter((a) => a.description?.toLowerCase() !== 'advance' && a.description?.toLowerCase() !== 'carry forward')
      .reduce((sum, a) => sum + Number(a.amount || 0), 0);

    setAdvance(normal);
    setOtherAdvance(others);
  };

  const handleCalculate = () => {
    const total = parseFloat(price || 0) * totalQty;
    const final = total - advance - otherAdvance;
    setCalculatedAmount(total.toFixed(2));
    setFinalAmount(final.toFixed(2));
  };

  const handleCarryForward = async () => {
    let nextMonth = parseInt(selectedMonth) + 1;
    let year = parseInt(selectedYear);

    if (nextMonth > 12) {
      nextMonth = 1;
      year += 1;
    }

    const dateStr = `${year}-${String(nextMonth).padStart(2, '0')}-03`;
    const carryAmount = Math.abs(finalAmount);
    const description = 'Carry Forward';

    try {
      const res = await fetch(`https://kmtea.onrender.com/advances`);
      const allAdvances = await res.json();

      const existing = allAdvances.find(
        (a) =>
          a.farmid === selectedFarmid &&
          a.date.startsWith(`${year}-${String(nextMonth).padStart(2, '0')}`) &&
          a.description === description
      );

      const response = await fetch(existing ? 'https://kmtea.onrender.com/update-advance' : 'https://kmtea.onrender.com/add-advance', {
        method: existing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farmid: selectedFarmid,
          amount: carryAmount,
          date: dateStr,
          description
        }),
      });

      if (response.ok) {
        alert('Carry forward recorded.');
        setCarryForwarded(true);
        fetchAdvances();
      } else {
        alert('Error saving carry forward.');
      }
    } catch (err) {
      console.error('Carry forward error:', err);
      alert('Something went wrong.');
    }
  };

  const handlePay = () => {
    navigate('/dashboard/payment', {
      state: { selectedFarmid, amount: finalAmount },
    });
  };

  return (
    <div className="farmer-month__container">
      <h2>Monthly Total Quantity</h2>

      <div className="farmer-month__filters">
        <select value={selectedFarmid} onChange={(e) => setSelectedFarmid(e.target.value)}>
          <option value="">Select Farmer</option>
          {farmers.map((f) => (
            <option key={f.farmid} value={f.farmid}>{f.name} ({f.farmid})</option>
          ))}
        </select>

        <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          <option value="">Month</option>
          {monthNames.map((name, index) => (
            <option key={index + 1} value={index + 1}>{name}</option>
          ))}
        </select>

        <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
          <option value="">Year</option>
          {Array.from({ length: 5 }, (_, i) => 2023 + i).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {selectedFarmid && selectedMonth && selectedYear && (
        <>
          <table className="farmer-month__table">
            <thead>
              <tr>
                <th>Farmer Name</th>
                <th>Farm ID</th>
                <th>Month</th>
                <th>Year</th>
                <th>Total Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{farmers.find((f) => f.farmid === selectedFarmid)?.name || 'Unknown'}</td>
                <td>{selectedFarmid}</td>
                <td>{monthNames[selectedMonth - 1]}, {selectedMonth}</td>
                <td>{selectedYear}</td>
                <td>{totalQty}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginTop: '20px' }}>
            <label>
              Enter Price (₹):{' '}
              <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} style={{ marginRight: '10px', width: '100px' }} />
            </label>
            <button onClick={handleCalculate}>OK</button>
          </div>

          {calculatedAmount && (
            <>
              <table className="farmer-month__table" style={{ marginTop: '20px' }}>
                <thead>
                  <tr>
                    <th>Total Quantity</th>
                    <th>Price (₹)</th>
                    <th>Total Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{totalQty}</td>
                    <td>{price}</td>
                    <td>{calculatedAmount}</td>
                  </tr>
                  <tr>
                    <td colSpan="2" style={{ textAlign: 'right' }}>Advance + Carry Forward (₹)</td>
                    <td>{advance}</td>
                  </tr>
                  <tr>
                    <td colSpan="2" style={{ textAlign: 'right' }}>Other Advances (₹)</td>
                    <td>{otherAdvance}</td>
                  </tr>
                  <tr>
                    <td colSpan="2" style={{ textAlign: 'right', fontWeight: 'bold' }}>Final Amount (₹)</td>
                    <td style={{ fontWeight: 'bold' }}>{finalAmount}</td>
                  </tr>
                </tbody>
              </table>

              {parseFloat(finalAmount) >= 0 && (
               <a href='https://infinitynxt.icicibank.com/in/login-page' > <button className="pay-btn" onClick={handlePay}>Pay</button> 
 </a>
              )}

              {parseFloat(finalAmount) < 0 && !carryForwarded && (
                <button style={{ width: '300px', marginLeft: '25%', backgroundColor: 'orange', color: 'white' }} onClick={handleCarryForward}>
                  Carry Forward ₹{Math.abs(finalAmount)}
                </button>
              )}

              {carryForwarded && (
                <p style={{ textAlign: 'center', color: 'green', marginTop: '10px' }}>
                  Carry forward recorded successfully!
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyQtyTotal;
